// src/features/riskmize/useRiskIncidents.js
//
// Polls GET /api/riskmize/incidents on a 60s timer when the layer is enabled.
// Consumers (Phase 5 Live Map) should pass a `bboxRef` — a React ref whose
// `.current` is a `[west, south, east, north]` array representing the current
// map viewport. Ref-based (rather than value-based) so map pans don't retrigger
// the whole effect; the poll just reads `.current` on each tick.
//
// @param {React.MutableRefObject<[number,number,number,number]|undefined>} bboxRef
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { riskmizeApi } from './riskmize.api.js';
import { riskmizeActions } from '../../store/slices/riskmizeSlice.js';

const POLL_MS = 60_000;

function timeRangeToFrom(range) {
  const now = Date.now();
  const day = 24 * 3600 * 1000;
  switch (range) {
    case '24h': return new Date(now - day);
    case '48h': return new Date(now - 2 * day);
    case '7d':  return new Date(now - 7 * day);
    case '30d': return new Date(now - 30 * day);
    default:    return undefined;
  }
}

export function useRiskIncidents(bboxRef) {
  const dispatch = useDispatch();
  const layerEnabled = useSelector((s) => s.riskmize.layerEnabled);
  const filters = useSelector((s) => s.riskmize.filters);

  const filterSeverityKey = useMemo(
    () => (filters.severity ?? []).join(','),
    [filters.severity]
  );

  useEffect(() => {
    if (!layerEnabled) return undefined;
    let cancelled = false;
    async function load() {
      try {
        dispatch(riskmizeActions.setLoading(true));
        const fc = await riskmizeApi.listIncidents({
          bbox: bboxRef?.current,
          from: timeRangeToFrom(filters.timeRange),
          severity: filters.severity?.length ? filters.severity : undefined,
          // Category filter is applied client-side in IncidentsDrawer so the
          // dropdown options stay stable (they're derived from the loaded set).
        });
        if (cancelled) return;
        dispatch(riskmizeActions.setIncidents(Array.isArray(fc) ? fc : fc?.features ?? []));
        dispatch(riskmizeActions.setError(null));
      } catch (err) {
        if (cancelled) return;
        dispatch(riskmizeActions.setError(String(err?.message ?? err)));
      } finally {
        if (!cancelled) dispatch(riskmizeActions.setLoading(false));
      }
    }
    load();
    const id = setInterval(load, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [layerEnabled, filterSeverityKey, filters.timeRange, dispatch]);
}
